import React, { createContext, useEffect, useRef, useState } from "react"
import { toast } from "react-toastify"
import { io, Socket } from "socket.io-client"

import { ADMIN_LOCALES } from "constants/locales"
import { EVENT_KYC_FILES_COUNT, EVENT_RISK_ALERT } from "constants/socket-events"

import { authService } from "services/auth/auth.service"
import { isLoginResponseWithMFA } from "services/auth/type"
import { kycService } from "services/kyc/kyc.service"
import { preferencesService } from "services/preferences/preferences.service"
import { BonusCatalogMode, TenantMode, tenantModeService } from "services/tenant/tenant-mode.service"
import { transactionService } from "services/transaction/transaction.service"
import { User } from "services/user/type"
import { userService } from "services/user/user.service"

import { parseError } from "util/api"
import { isValidTimezone } from "util/date"

import { setTenantMode } from "config/featureFlags"

import i18n from "../i18n"
import {
  getterAdminLanguage,
  getterAdminTimezone,
  getterAuthenticated,
  getterDarkMode,
  setterAdminLanguage,
  setterAdminTimezone,
  setterAuthenticated,
  setterDarkMode
} from "../storage/cookies"

import { createCtx } from "./Context"

type PropsContextType = {
  user: User | null
  authenticated: boolean
  kycFilesCount: number
  submittedWithdrawalsCount: number
  pendingReviewCount: number
  // The tenant's migration mode from `GET /admin/tenant-mode`; `null` until resolved (monolith view).
  tenantMode: TenantMode | null
  // D01: `v2` → bonus-service owns the catalog (show Bonus V2 only); `legacy` → monolith owns it
  // (show the legacy Bonuses page only); `null` → not yet resolved.
  bonusCatalog: BonusCatalogMode | null
  setUser: any
  refreshUser: () => Promise<void>
  onUserLogin: any
  onUserLoginTwoFactor: any
  onUserRemoveTwoFactorByRecoveryCode: any
  onUserLogout: any
  onKYCFilesCount: any
  onSubmittedWithdrawalsCount: any
  darkMode: boolean
  onDarkModeChange: (value: boolean) => void
  language: string
  setLanguage: React.Dispatch<React.SetStateAction<string>>
  timezone: string
  setTimezone: React.Dispatch<React.SetStateAction<string>>
  settingsOpen: boolean
  setSettingsOpen: (open: boolean) => void
}

export const [useProps, CtxProvider] = createCtx<PropsContextType>()

export const PropsContext = createContext<PropsContextType | undefined>(undefined)

export const PropsProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null)
  const [darkMode, setDarkMode] = useState<boolean>(getterDarkMode())
  const [language, setLanguage] = useState<string>(getterAdminLanguage())
  const [timezone, setTimezone] = useState<string>(getterAdminTimezone())
  const [authenticated, setAuthenticated] = useState<boolean>(false)
  const [initialised, setInitialised] = useState(false)
  const [kycFilesCount, setKYCFilesCount] = useState(0)
  const [submittedWithdrawalsCount, setSubmittedWithdrawalsCount] = useState(0)
  const [tenantMode, setTenantModeState] = useState<TenantMode | null>(null)
  const bonusCatalog: BonusCatalogMode | null = tenantMode?.bonus_catalog ?? null
  const [socket, setSocket] = useState<Socket | null>(null)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const serverPrefsRef = useRef<{ language?: string; timezone?: string }>({})
  const [pendingReviewCount] = useState(0)

  useEffect(() => {
    const onLoad = async (): Promise<void> => {
      if (getterAuthenticated()) {
        try {
          const user = await userService.getUser()
          // Any user with at least one permission is allowed in the admin
          // panel. The backend resolves permissions from RolePermission rows,
          // so this replaces the legacy `apAccess` role-list check.
          if (user && (user.permissions?.length ?? 0) > 0) {
            // Resolve the mode before `initialised`: column sets and tabs are computed at render time
            // and must not flash the monolith view first.
            await resolveTenantMode()
            setUser(user)
            setAuthenticated(true)
          } else {
            setterAuthenticated(false)
          }
        } catch {
          // Session expired server-side — clear the local flag silently
          setterAuthenticated(false)
        }
      }
      setInitialised(true)

      if (process.env.REACT_APP_BASE_API_URL) {
        setSocket(
          io(process.env.REACT_APP_BASE_API_URL, {
            query: {},
            rejectUnauthorized: false,
            transports: ["websocket"],
            withCredentials: true
            // path: "/api/socket.io"
          })
        )
      }
    }

    onLoad()
  }, [])

  useEffect(() => {
    if (authenticated) {
      onKYCFilesCount()
      onSubmittedWithdrawalsCount()
      onLoadPreferences()
    }
  }, [authenticated])

  useEffect(() => {
    if (socket) {
      onSocket()
    }
  }, [socket])

  useEffect(() => {
    setterAdminLanguage(language)
    i18n.changeLanguage(language)
    if (authenticated && language !== serverPrefsRef.current.language) {
      serverPrefsRef.current.language = language
      preferencesService.updateMyPreferences({ language })
    }
  }, [language])

  useEffect(() => {
    setterAdminTimezone(timezone)
    if (authenticated && timezone !== serverPrefsRef.current.timezone) {
      serverPrefsRef.current.timezone = timezone
      preferencesService.updateMyPreferences({ timezone })
    }
  }, [timezone])

  const onSocket = () => {
    if (!socket) return

    socket.on("connect", () => {
      // console.log("Socket connected")
    })
    socket.on("connect_error", (err: Error) => {
      // eslint-disable-next-line no-console
      console.error(err)
    })

    socket.on(EVENT_KYC_FILES_COUNT, (res: { count: number }) => {
      setKYCFilesCount(res.count ?? 0)
    })

    socket.on(EVENT_RISK_ALERT, (alert: { type: string; user_id: string; username: string; score: number; level: string }) => {
      const label = alert.type === "CRITICAL_RISK" ? "🚨 Critical Risk" : "⚠️ High Risk"
      toast.warn(`${label}: ${alert.username} (Score: ${alert.score})`)
    })
  }

  const onUserLogin = async (username: string, password: string, turnstileToken?: string): Promise<User | null | { mfa: boolean }> => {
    try {
      const response = await authService.signIn({ username, password, "cf-turnstile-response": turnstileToken })

      // Check if MFA is required
      if (isLoginResponseWithMFA(response)) {
        return { mfa: true }
      }

      // If not MFA, proceed to get user info
      const user = await userService.getUser()
      if (!user) {
        // Session was created on server but getUser returned empty — clean it up
        // silently (fire-and-forget: if cookie issue exists, this will also fail
        // but without producing extra error toasts)
        authService.logout().catch(() => {})
        toast.error("Failed to retrieve user information")
        return null
      }

      if ((user.permissions?.length ?? 0) > 0) {
        await resolveTenantMode()
        setUser(user)
        setterAuthenticated(true)
        setAuthenticated(true)
        return user
      }

      toast.error("You don't have permission to access this page")
      await authService.logout()
      return null
    } catch (error) {
      parseError(error)
      return null
    }
  }

  const onUserLoginTwoFactor = async (twoFactorCode: string): Promise<User | null> => {
    try {
      await authService.signInWithTwoFactor(twoFactorCode)
      const user = await userService.getUser()
      if (user) {
        if ((user.permissions?.length ?? 0) > 0) {
          await resolveTenantMode()
          setUser(user)
          setterAuthenticated(true)
          setAuthenticated(true)
          return user
        }
        toast.error("You don't have permission to access this page")
        await authService.logout()
      }
      return null
    } catch (error) {
      parseError(error)
      return null
    }
  }

  const onUserRemoveTwoFactorByRecoveryCode = async (recoveryCode: string): Promise<boolean> => {
    try {
      const { disabled } = await authService.removeTwoFactorByRecoveryCode(recoveryCode)
      return disabled
    } catch (error) {
      parseError(error)
      return false
    }
  }

  const refreshUser = async (): Promise<void> => {
    try {
      const fresh = await userService.getUser()
      if (fresh) setUser(fresh)
    } catch (error) {
      parseError(error)
    }
  }

  const onUserLogout = async (): Promise<void> => {
    await authService.logout()
    setterAuthenticated(false)
    setUser(null)
    setAuthenticated(false)
    applyTenantMode(null)
  }

  const onDarkModeChange = (value: boolean): void => {
    setDarkMode(value)
    setterDarkMode(value)
  }

  const onKYCFilesCount = async () => {
    try {
      const response = await kycService.getKYCFilesCount()
      setKYCFilesCount(response.count)
    } catch (error) {
      // Non-critical badge counter — don't disrupt UX, but log for debugging
      console.error("[KYC count] failed to refresh:", error)
    }
  }

  const onLoadPreferences = async () => {
    const prefs = await preferencesService.getMyPreferences()
    if (!prefs) return

    if ((ADMIN_LOCALES as string[]).includes(prefs.language)) {
      serverPrefsRef.current.language = prefs.language
      setLanguage(prefs.language)
    }
    if (isValidTimezone(prefs.timezone)) {
      serverPrefsRef.current.timezone = prefs.timezone
      setTimezone(prefs.timezone)
    }
  }

  const applyTenantMode = (mode: TenantMode | null): void => {
    setTenantMode(mode)
    setTenantModeState(mode)
  }

  // The tenant's migration mode gates MS-only surfaces (native columns, Bets tabs, Bonus V2,
  // currency on balance edits). A failed fetch leaves it `null` = monolith view: MS-only tooling
  // must never appear on a tenant whose mode is unknown.
  const resolveTenantMode = async (): Promise<void> => {
    try {
      applyTenantMode((await tenantModeService.getTenantMode()) ?? null)
    } catch (error) {
      applyTenantMode(null)
      console.error("[Tenant mode] failed to resolve — rendering the monolith view:", error)
    }
  }

  const onSubmittedWithdrawalsCount = async () => {
    try {
      const response = await transactionService.pendingWithdrawalsCount()
      // SOFT-551: set the badge to ANY numeric count, including 0 — a legitimate 0 must clear a
      // stale non-zero badge so it matches the empty Submitted-withdrawals page (`if (count)` skipped
      // 0 and left the badge stuck). On the fail-closed `{ count: null, unavailable: true }` (FS
      // down) hide the badge (reset to 0) rather than keep a misleading stale number.
      if (response?.unavailable || typeof response?.count !== "number") {
        setSubmittedWithdrawalsCount(0)
      } else {
        setSubmittedWithdrawalsCount(response.count)
      }
    } catch (error) {
      // Non-critical badge counter — don't disrupt UX, but log for debugging
      console.error("[Withdrawals count] failed to refresh:", error)
    }
  }

  if (!initialised)
    return (
      <div className="grid place-items-center h-screen">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    )

  return (
    <>
      <CtxProvider
        value={{
          user,
          authenticated,
          kycFilesCount,
          submittedWithdrawalsCount,
          pendingReviewCount,
          tenantMode,
          bonusCatalog,
          setUser,
          refreshUser,
          onUserLogin,
          onUserLoginTwoFactor,
          onUserRemoveTwoFactorByRecoveryCode,
          onUserLogout,
          onKYCFilesCount,
          onSubmittedWithdrawalsCount,
          darkMode,
          onDarkModeChange,
          language,
          setLanguage,
          timezone,
          setTimezone,
          settingsOpen,
          setSettingsOpen
        }}
      >
        {children}
      </CtxProvider>
    </>
  )
}
